import { useCallback, useMemo, useState } from 'react';
import { IStepConfig } from '../../../Form/FormStepper/FormStepper';
import YourDetails from '../Steps/YourDetails';
import YourExperience from '../Steps/YourExperience';
import ClinicalContact from '../Steps/ClinicalContact';
import Supervisors from '../Steps/Supervisors';
import Documents from '../Steps/Documents';
import SubmitAndPay from '../Steps/SubmitAndPay';
import IAccreditationApplicationForm from '../../../../interfaces/IAccreditationApplicationForm';
import { IAccreditationApplication } from '../../../../interfaces/IAccreditationApplication';
import { useApplicationValidator } from './useApplicationValidator';
import Activities from '../Steps/Activities';
import ActivityDetails from '../Steps/ActivityDetails';

const useApplicationFormStepsConfig = (
    formData: IAccreditationApplicationForm,
    application: IAccreditationApplication
) => {
    const formValidator = useApplicationValidator(application);
    const [currentStep, setCurrentStep] = useState(1);
    const [validated, setValidated] = useState<boolean>(false);

    // Memoize validation handlers
    const handleValidateExperience = useCallback(() => {
        return formValidator.validateExperience(formData);
    }, [formValidator, formData]);

    const handleValidateClinicalContacts = useCallback(() => {
        return formValidator.validateClinicalContacts(formData);
    }, [formValidator, formData]);

    const handleValidateSupervisors = useCallback(() => {
        return formValidator.validateSupervisors(formData);
    }, [formValidator, formData]);

    const handleValidateDocuments = useCallback(() => {
        return formValidator.validateDocuments(formData);
    }, [formValidator, formData]);

    const handleValidateActivities = useCallback(() => {
        return formValidator.validateActivities(formData);
    }, [formValidator, formData]);

    const handleValidateActivityDetails = useCallback(() => {
        return formValidator.validateActivityDetails(formData);
    }, [formValidator, formData]);

    // Memoize the steps configuration
    const stepsConfig = useMemo<IStepConfig[]>(
        () => [
            {
                title: 'Your details',
                showSaveProgressButton: false,
                content: YourDetails,
            },
            {
                title: 'Your experience',
                content: YourExperience,
                validate: handleValidateExperience,
            },
            ...(application.isRenewal
                ? [
                      {
                          title: 'Activities',
                          content: Activities,
                          validate: handleValidateActivities,
                      },
                      {
                          title: 'Activity Details',
                          content: ActivityDetails,
                          validate: handleValidateActivityDetails,
                      },
                      {
                          title: 'Supervisors',
                          content: Supervisors,
                          validate: handleValidateSupervisors,
                      },
                  ]
                : [
                      {
                          title: 'Clinical contact',
                          content: ClinicalContact,
                          validate: handleValidateClinicalContacts,
                      },
                      {
                          title: 'Supervisors',
                          content: Supervisors,
                          validate: handleValidateSupervisors,
                      },
                  ]),
            {
                title: 'Documents',
                content: Documents,
                validate: handleValidateDocuments,
            },
            {
                title: 'Review & Submit',
                showNextButton: false,
                showSaveProgressButton: false,
                content: SubmitAndPay,
            },
        ],
        [
            application.isRenewal,
            handleValidateExperience,
            handleValidateClinicalContacts,
            handleValidateSupervisors,
            handleValidateDocuments,
            handleValidateActivities,
            handleValidateActivityDetails,
        ]
    );

    // Memoize the navigation functions
    const handleSetStep = useCallback(
        (step: number) => {
            let errors: string[] = [];
            setValidated(false);

            if (step > currentStep) {
                const sc = stepsConfig[currentStep - 1];
                if (sc.validate) {
                    errors = sc.validate();
                    setValidated(true);
                }
            }

            if (!errors.length) {
                setCurrentStep(step);
            }

            return errors;
        },
        [currentStep, stepsConfig]
    );

    const handleNextStep = useCallback(
        (back?: boolean) => {
            return handleSetStep(
                back ? Math.max(currentStep - 1, 1) : Math.min(currentStep + 1, stepsConfig.length)
            );
        },
        [handleSetStep, currentStep, stepsConfig.length]
    );

    // Memoize the skip validation function
    const handleCanSkipToStep = useCallback(
        (step: number) => {
            if (application.isPaid) return true;
            if (step < 2) return true;

            // We'll use if/else instead of switch/fallthrough for better readability
            if (step >= 6) {
                return formValidator.validateForm(formData).length > 0;
            }

            if (step >= 5) {
                if (formValidator.validateDocuments(formData).length > 0) return false;
            }

            if (step >= 4) {
                if (
                    (application.isRenewal &&
                        formValidator.validateActivityDetails(formData).length > 0) ||
                    (!application.isRenewal &&
                        formValidator.validateSupervisors(formData).length > 0)
                ) {
                    return false;
                }
            }

            if (step >= 3) {
                if (
                    (application.isRenewal &&
                        formValidator.validateActivities(formData).length > 0) ||
                    (!application.isRenewal &&
                        formValidator.validateClinicalContacts(formData).length > 0)
                ) {
                    return false;
                }
            }

            return formValidator.validateExperience(formData).length === 0;
        },
        [application.isPaid, application.isRenewal, formValidator, formData]
    );

    // Memoize the returned object
    return useMemo(
        () => ({
            stepsConfig,
            handleCanSkipToStep,
            formValidator,
            currentStep,
            handleNextStep,
            handleSetStep,
            validated,
        }),
        [
            stepsConfig,
            handleCanSkipToStep,
            formValidator,
            currentStep,
            handleNextStep,
            handleSetStep,
            validated,
        ]
    );
};

export default useApplicationFormStepsConfig;
